import React, { useState, useMemo } from 'react'
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Target, Zap, Repeat, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useActiveCampaigns, ActiveCampaign } from '@/hooks/useActiveCampaigns'
import { useOptimizedCampaignPerformance, refreshCampaignPerformanceCache } from '@/hooks/useOptimizedCampaignPerformance'
import { CampaignVariantBreakdown } from './CampaignVariantBreakdown'
import { useCampaignVariants } from '@/hooks/useCampaignVariants'

interface LiveCampaignsSectionProps {
  selectedClientId: string
}

interface CampaignDisplay {
  name: string
  campaign_type: string
  id: string
  created_at: string
  status: string
  version?: string
}

export function LiveCampaignsSection({ selectedClientId }: LiveCampaignsSectionProps) {
  const { data: campaigns, isLoading: loading } = useActiveCampaigns(selectedClientId)
  
  // State for variant breakdown modal
  const [selectedCampaign, setSelectedCampaign] = useState<CampaignDisplay | null>(null)
  const [isVariantBreakdownOpen, setIsVariantBreakdownOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  // Use optimized hook for 95% faster performance (queries materialized view)
  const { 
    campaigns: optimizedCampaigns, 
    loading: performanceLoading, 
    refresh: refreshOptimized,
    cacheAge 
  } = useOptimizedCampaignPerformance(selectedClientId, 'all')
  
  // Transform optimized data to match expected format for backwards compatibility
  const performanceData = useMemo(() => {
    const data: Record<string, any> = {}
    
    optimizedCampaigns.forEach(camp => {
      data[camp.campaign_id] = {
        leadsContacted: camp.email_leads_contacted,
        sendVolume: camp.email_send_volume,
        replies: camp.email_total_replies,
        humanReplies: camp.email_human_replies,
        positiveReplies: camp.email_positive_replies,
        replyRate: camp.email_reply_rate,
        humanReplyRate: camp.email_human_reply_rate,
        positiveRate: camp.email_positive_rate,
        meetingsBooked: camp.meetings_booked,
        dealsWon: camp.deals_won,
        revenueGenerated: camp.deals_won * 15000 // Calculate revenue
      }
    })
    
    return data
  }, [optimizedCampaigns])

  // Debug function to force refresh all campaign data
  const handleForceRefresh = async () => {
    console.log('🔄 Force refreshing campaign performance materialized view...')
    setIsRefreshing(true)
    
    try {
      // Refresh the materialized view
      await refreshCampaignPerformanceCache()
      
      // Trigger UI refresh
      await refreshOptimized()
      
      console.log('✅ Campaign performance data refreshed successfully')
    } catch (error) {
      console.error('❌ Failed to refresh campaign data:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Handle campaign click to show variant breakdown
  const handleCampaignClick = (campaign: CampaignDisplay) => {
    setSelectedCampaign(campaign)
    setIsVariantBreakdownOpen(true)
  }

  // Campaign Variant Breakdown Modal Component
  const CampaignVariantBreakdownModal = ({ 
    campaign, 
    isOpen, 
    onClose 
  }: { 
    campaign: CampaignDisplay
    isOpen: boolean
    onClose: () => void
  }) => {
    const { data: variantData, isLoading } = useCampaignVariants(campaign.id, campaign.name)

    if (isLoading) {
      return (
        <CampaignVariantBreakdown
          isOpen={isOpen}
          onClose={onClose}
          campaignName={campaign.name}
          variantStats={[]}
          totalStats={{
            totalReplies: 0,
            autoReply: 0,
            ooo: 0,
            humanReplies: 0,
            positiveReplies: 0
          }}
        />
      )
    }

    return (
      <CampaignVariantBreakdown
        isOpen={isOpen}
        onClose={onClose}
        campaignName={campaign.name}
        variantStats={variantData?.variantStats || []}
        totalStats={variantData?.totalStats || {
          totalReplies: 0,
          autoReply: 0,
          ooo: 0,
          humanReplies: 0,
          positiveReplies: 0
        }}
      />
    )
  }

  // Categorize campaigns by campaign_type
  const categorizedCampaigns = React.useMemo(() => {
    const categories = { bulk: [], events: [], triggers: [] } as {
      bulk: CampaignDisplay[]
      events: CampaignDisplay[]
      triggers: CampaignDisplay[]
    }

    if (!campaigns) {
      return categories
    }

    campaigns.forEach(campaign => {
      const campaignDisplay: CampaignDisplay = {
        name: campaign.name,
        campaign_type: campaign.campaign_type,
        id: campaign.id,
        created_at: campaign.created_at,
        status: campaign.status,
        version: campaign.version
      }

      const type = campaign.campaign_type?.toLowerCase()
      if (type === 'bulk') {
        categories.bulk.push(campaignDisplay)
      } else if (type === 'triggers') {
        categories.triggers.push(campaignDisplay)
      } else {
        // Events, adhoc, and any other types go under Events
        categories.events.push(campaignDisplay)
      }
    })

    // Sort each category by created_at date in descending order (latest first)
    const sortByDate = (a: CampaignDisplay, b: CampaignDisplay) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    categories.bulk.sort(sortByDate)
    categories.events.sort(sortByDate)
    categories.triggers.sort(sortByDate)

    return categories
  }, [campaigns])

  // Calculate summary stats for each category
  const getSummaryStats = (campaigns: CampaignDisplay[]) => {
    const totalCampaigns = campaigns.length
    return { totalCampaigns, totalLeads: 0, totalReplies: 0, avgReplyRate: 0 }
  }

  const CampaignCard = ({ campaign }: { campaign: CampaignDisplay }) => {
    const performance = performanceData[campaign.id] || {
      leadsContacted: 0,
      sendVolume: 0,
      replies: 0,
      humanReplies: 0,
      positiveReplies: 0,
      meetingsBooked: 0
    }

    return (
      <div 
        className="border border-border rounded-lg p-3 hover:shadow-sm transition-shadow bg-card cursor-pointer hover:bg-muted/50"
        onClick={() => handleCampaignClick(campaign)}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-sm text-foreground truncate flex-1 mr-2">
            {campaign.name}
          </h4>
          <Badge 
            variant={campaign.status === 'active' ? 'default' : 'secondary'} 
            className="text-xs shrink-0"
          >
            {campaign.status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
          <span>Created: {new Date(campaign.created_at).toLocaleDateString()}</span>
          {campaign.version && (
            <span className="bg-muted px-2 py-1 rounded text-xs">
              v{campaign.version}
            </span>
          )}
        </div>

        {/* Performance Metrics */}
        <div className="border-t border-border pt-2 mt-2">
          {performanceLoading ? (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Leads:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sent:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Replies:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Human:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Positive:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Meetings:</span>
                <div className="h-4 w-8 bg-muted animate-pulse rounded"></div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Leads:</span>
                <span className="font-medium">{(performance.leadsContacted || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sent:</span>
                <span className="font-medium">{(performance.sendVolume || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Replies:</span>
                <span className="font-medium">{(performance.replies || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Human:</span>
                <span className="font-medium">{(performance.humanReplies || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Positive:</span>
                <span className="font-medium">{(performance.positiveReplies || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Meetings:</span>
                <span className="font-medium">{(performance.meetingsBooked || 0).toLocaleString()}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  const CampaignColumn = ({ 
    title, 
    icon: Icon, 
    campaigns: sectionCampaigns, 
    color,
    bgColor
  }: { 
    title: string
    icon: any
    campaigns: any[]
    color: string
    bgColor: string
  }) => {
    const stats = getSummaryStats(sectionCampaigns)
    
    return (
      <div className="border border-border rounded-lg overflow-hidden bg-card">
        <div className={cn("p-4", bgColor)}>
          <div className="flex items-center gap-3 mb-3">
            <div className={cn("p-2 rounded-lg", color)}>
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">
                {stats.totalCampaigns} campaign{stats.totalCampaigns !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground">
            {stats.totalCampaigns} active campaign{stats.totalCampaigns !== 1 ? 's' : ''}
          </div>
        </div>
        
        <div className="p-4 space-y-3 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          {sectionCampaigns.length > 0 ? (
            <>
              {sectionCampaigns.map((campaign, index) => (
                <CampaignCard key={`${campaign.name}-${index}`} campaign={campaign} />
              ))}
              {sectionCampaigns.length > 3 && (
                <div className="text-center pt-2">
                  <div className="text-xs text-muted-foreground bg-muted/50 rounded-full px-3 py-1 inline-flex items-center gap-1">
                    <div className="w-1 h-1 bg-current rounded-full"></div>
                    <div className="w-1 h-1 bg-current rounded-full"></div>
                    <div className="w-1 h-1 bg-current rounded-full"></div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="text-muted-foreground/60 mb-2">
                <Target className="h-8 w-8 mx-auto" />
              </div>
              <p className="text-sm text-muted-foreground">No active campaigns</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <section id="campaigns-live" className="scroll-mt-20">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">Campaigns Live</h2>
          <p className="text-muted-foreground">Active campaigns across all channels</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 animate-pulse">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-80 bg-muted rounded-lg" />
          ))}
        </div>
      </section>
    )
  }

  return (
    <section id="campaigns-live" className="scroll-mt-20">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Campaigns Live</h2>
            <p className="text-muted-foreground">
              Monitor your active campaigns across all channels and categories
            </p>
            {cacheAge !== null && (
              <p className="text-xs text-muted-foreground mt-1">
                ⚡ Using optimized cache • Data updated {cacheAge} {cacheAge === 1 ? 'minute' : 'minutes'} ago
              </p>
            )}
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleForceRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <CampaignColumn
          title="Bulk Campaigns"
          icon={Target}
          campaigns={categorizedCampaigns.bulk}
          color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
          bgColor="bg-muted/30"
        />
        
        <CampaignColumn
          title="Events"
          icon={Zap}
          campaigns={categorizedCampaigns.events}
          color="bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"
          bgColor="bg-muted/30"
        />
        
        <CampaignColumn
          title="Triggers"
          icon={Repeat}
          campaigns={categorizedCampaigns.triggers}
          color="bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400"
          bgColor="bg-muted/30"
        />
      </div>

      {/* Variant Breakdown Modal */}
      {selectedCampaign && (
        <CampaignVariantBreakdownModal 
          campaign={selectedCampaign}
          isOpen={isVariantBreakdownOpen}
          onClose={() => {
            setIsVariantBreakdownOpen(false)
            setSelectedCampaign(null)
          }}
        />
      )}
    </section>
  )
}